import { useMemo } from "react";

import { useEffectiveEntityRight } from "@/domain/_shared/entity-permission/hooks/api/use-effective-entity-right";
import { useFeatureFlagSimple } from "@/domain/_shared/feature-flag/hooks/api/use-feature-flag-simple";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";

import { ActionType, ResourceType } from "../models/permission-types";
import { getEntityTypeForResource, hasRequiredRight } from "../models/permission-v2-mapping";

import { useCheckPermissionList } from "./api/use-check-permission-list";

interface UseEntityPermissionsProps {
  entityId: string;
  resource: ResourceType;
  enabled?: boolean;
  useKeepPreviousData?: boolean;
}

export const useEntityPermissions = ({ entityId, resource, enabled = true, useKeepPreviousData = false }: UseEntityPermissionsProps) => {
  const entityType = getEntityTypeForResource(resource) ?? "";
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const { isEnabled: isV2Enabled } = useFeatureFlagSimple({ id: "enable-permission-v2" });

  const permissions = useMemo(
    () => [
      { resource, action: "write" as ActionType, resourceId: entityId },
      { resource, action: "manage" as ActionType, resourceId: entityId },
      { resource, action: "delete" as ActionType, resourceId: entityId },
    ],
    [resource, entityId],
  );

  const {
    permissionRecord,
    loading: v1Loading,
    error: v1Error,
  } = useCheckPermissionList({
    permissions,
    enabled: enabled && !!entityId && !isV2Enabled,
  });

  const {
    effectiveRight,
    loading: v2Loading,
    error: v2Error,
  } = useEffectiveEntityRight({
    entityId,
    entityType,
    workspaceId: currentWorkspace.id,
    enabled: enabled && !!entityId && isV2Enabled,
    useKeepPreviousData,
  });

  if (isV2Enabled) {
    return {
      canWrite: hasRequiredRight(effectiveRight, "write"),
      canManage: hasRequiredRight(effectiveRight, "manage"),
      canDelete: hasRequiredRight(effectiveRight, "delete"),
      loading: v2Loading,
      error: v2Error,
    };
  }

  const resourcePermissions = permissionRecord[resource] || {};

  return {
    canWrite: resourcePermissions.write || false,
    canManage: resourcePermissions.manage || false,
    canDelete: resourcePermissions.delete || false,
    loading: v1Loading,
    error: v1Error,
  };
};
